import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Video, Shield, Users, Zap, Target, Heart, Globe, Award } from "lucide-react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";

const teamValues = [
  {
    icon: Shield,
    title: "Privacy First",
    description: "We never sell your data. Your meetings are encrypted and protected by industry-leading security measures.",
  },
  {
    icon: Heart,
    title: "User-Centric Design",
    description: "Every feature is built with users in mind, ensuring intuitive experiences across all devices.",
  },
  {
    icon: Globe,
    title: "Global Accessibility",
    description: "Available in 150+ countries with localized support and infrastructure for optimal performance worldwide.",
  },
  {
    icon: Target,
    title: "Continuous Innovation",
    description: "We're constantly improving and adding features based on user feedback and emerging technologies.",
  },
];

const milestones = [
  { year: "2021", event: "MeetHub founded with a vision for secure, simple video conferencing" },
  { year: "2022", event: "Reached 100,000 active users and launched breakout rooms" },
  { year: "2023", event: "Introduced AI-powered live captions and transcription" },
  { year: "2024", event: "Expanded to 150+ countries with 500,000+ active users" },
];

export default function About() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5">
      <Navigation />
      <div className="container mx-auto px-4 py-8 pt-24">

        <div className="max-w-5xl mx-auto space-y-12">
          {/* Hero Section */}
          <div className="text-center space-y-4">
            <div className="flex items-center justify-center mb-4">
              <Video className="h-12 w-12 text-primary mr-3" />
              <h1 className="text-4xl font-bold">About MeetHub</h1>
            </div>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Empowering teams worldwide with secure, seamless video conferencing
            </p>
          </div>

          {/* Mission Section */}
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl">Our Mission</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground text-lg">
                MeetHub is a modern, secure, and scalable video conferencing platform designed to power seamless communication for teams, educators, creators, and communities around the world.
              </p>
              <p className="text-muted-foreground">
                Built as a privacy-first alternative to traditional video conferencing tools, MeetHub focuses on security, flexibility, and exceptional user experience. We empower organizations of all sizes with a reliable solution for virtual meetings, webinars, live events, and collaborative sessions.
              </p>
              <p className="text-muted-foreground">
                Our goal is simple: make professional video conferencing accessible to everyone while never compromising on privacy or security.
              </p>
            </CardContent>
          </Card>

          {/* Stats Section */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { value: "500K+", label: "Active Users" },
              { value: "10M+", label: "Meetings Hosted" },
              { value: "150+", label: "Countries" },
              { value: "99.9%", label: "Uptime" },
            ].map((stat) => (
              <Card key={stat.label} className="text-center p-6">
                <div className="text-3xl font-bold text-primary mb-1">{stat.value}</div>
                <div className="text-sm text-muted-foreground">{stat.label}</div>
              </Card>
            ))}
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <Shield className="h-8 w-8 text-primary mb-2" />
                <CardTitle>Enterprise-Grade Security</CardTitle>
                <CardDescription>Your privacy is our priority</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  End-to-end encryption, GDPR & CCPA compliance, SOC 2 certification, and enterprise-grade security measures to protect your communications at every level.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Users className="h-8 w-8 text-primary mb-2" />
                <CardTitle>Built for Collaboration</CardTitle>
                <CardDescription>Tools that bring teams together</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Real-time chat, screen sharing with annotations, cloud recording, breakout rooms, polls, Q&A, and everything your team needs to collaborate effectively.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Zap className="h-8 w-8 text-primary mb-2" />
                <CardTitle>Lightning Fast Performance</CardTitle>
                <CardDescription>Optimized for reliability</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Built on WebRTC technology with globally distributed infrastructure and smart bandwidth management for consistently smooth video calls, even on slower connections.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Award className="h-8 w-8 text-primary mb-2" />
                <CardTitle>Feature Rich Platform</CardTitle>
                <CardDescription>Everything you need in one place</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Virtual backgrounds, AI-powered live captions, automatic transcription, calendar integrations, analytics dashboard, and comprehensive admin controls.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Timeline Section */}
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl">Our Journey</CardTitle>
              <CardDescription>Key milestones in our growth</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {milestones.map((milestone, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex-shrink-0 w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-bold">{milestone.year}</span>
                    </div>
                    <div className="flex items-center">
                      <p className="text-muted-foreground">{milestone.event}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Values Section */}
          <div>
            <h2 className="text-2xl font-bold text-center mb-8">Our Core Values</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {teamValues.map((value, index) => (
                <Card key={index} className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-lg bg-primary/10">
                      <value.icon className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">{value.title}</h3>
                      <p className="text-muted-foreground text-sm">{value.description}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="text-center py-8">
              <h2 className="text-2xl font-bold mb-4">Ready to Transform Your Meetings?</h2>
              <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
                Join over 500,000 users who trust MeetHub for their video conferencing needs.
              </p>
              <a
                href="/auth"
                className="inline-flex items-center justify-center rounded-md bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                Get Started Free
              </a>
            </CardContent>
          </Card>
        </div>
      </div>
      <Footer />
    </div>
  );
}
